import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { _T } from '../../../include/appI18n';
import { BootstrapDatetimePicker } from '../../../include/react-components/BootstrapDatePicker';
import { BootstrapSelectPicker } from '../../../include/react-components/BootstrapSelectPicker';
import { BookingCustomerType, TouristDocumentType } from '../types';
import { parseAutofillDate, useAutofillSync } from './useAutofillSync';
const foreignPassportId = { name: _T.COMMON_DOC_TYPE_FOREIGN_PASSWORD, value: TouristDocumentType.RussianForeignPassport };
const russianId = { name: _T.COMMON_DOC_TYPE_PASSWORD_RF, value: TouristDocumentType.RussianPassport };
const birthCert = { name: _T.COMMON_DOC_TYPE_BIRTH_CERT, value: TouristDocumentType.RussianBirthCertificate };
const foreignDocumentId = { name: _T.COMMON_DOC_TYPE_FOREIGN_DOCUMENT, value: TouristDocumentType.ForeignDocument };
const getDocTypes = (useRussianDocuments, isRussian, age, realAge) => {
    if (useRussianDocuments) {
        if (age < 14)
            return [birthCert, foreignPassportId];
        if (realAge < 14)
            return [russianId, foreignPassportId, birthCert];
        return [russianId, foreignPassportId];
    }
    return [isRussian ? foreignPassportId : foreignDocumentId];
};
const getTouristHash = (t) => { var _a, _b, _c, _d, _e, _f; return `${(_a = t.gender) !== null && _a !== void 0 ? _a : ''}::${(_b = t.surname) !== null && _b !== void 0 ? _b : ''}::${(_c = t.name) !== null && _c !== void 0 ? _c : ''}::${t.birthDate ? moment(t.birthDate).format('YYYYMMDD') : ''}::${(_d = t.docType) !== null && _d !== void 0 ? _d : ''}::${(_e = t.docNumber) !== null && _e !== void 0 ? _e : ''}::${t.docExpirationDate ? moment(t.docExpirationDate).format('YYYYMMDD') : ''}::${t.docDate ? moment(t.docDate).format('YYYYMMDD') : ''}::${(_f = t.citizenCountryId) !== null && _f !== void 0 ? _f : ''}`; };
export function CustomerDetails(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const customer = props.booking.customer || { type: BookingCustomerType.Individual };
    const fallbackService = useMemo(() => props.booking.services.find(s => s.accommodation) || props.booking.services[0], [props.booking.services]);
    const fallbackAdults = (_b = (_a = fallbackService === null || fallbackService === void 0 ? void 0 : fallbackService.accommodation) === null || _a === void 0 ? void 0 : _a.adults) !== null && _b !== void 0 ? _b : 0;
    const fallbackServiceId = fallbackService === null || fallbackService === void 0 ? void 0 : fallbackService.id;
    const touristOptions = useMemo(() => {
        const seen = new Set();
        const options = [];
        props.booking.services.forEach(service => {
            var _a;
            (_a = service.tourists) === null || _a === void 0 ? void 0 : _a.forEach((t, idx) => {
                var _a, _b;
                const labelBase = `${((_a = t.surname) !== null && _a !== void 0 ? _a : '').trim()} ${((_b = t.name) !== null && _b !== void 0 ? _b : '').trim()}`.trim();
                const birth = t.birthDate ? moment(t.birthDate).format('L') : '';
                const label = birth ? `${labelBase} (${birth})`.trim() : labelBase;
                if (!label && !birth)
                    return;
                const hash = getTouristHash(t);
                if (seen.has(hash))
                    return;
                seen.add(hash);
                options.push({
                    value: `tourist:${service.id}:${idx}`,
                    label: label || `tourist:${service.id}:${idx}`,
                });
            });
        });
        if (!options.length && fallbackAdults > 0 && fallbackServiceId) {
            for (let i = 0; i < fallbackAdults; i++) {
                options.push({
                    value: `tourist:${fallbackServiceId}:${i}`,
                    label: `${_T.COMMON_TOURIST} ${i + 1}`,
                });
            }
        }
        return options;
    }, [props.booking.services, fallbackAdults, fallbackServiceId]);
    const typeOptions = useMemo(() => ([
        { value: `type:${BookingCustomerType.Individual}`, label: 'Физическое лицо' },
        { value: `type:${BookingCustomerType.SoleProprietor}`, label: 'ИП' },
        { value: `type:${BookingCustomerType.Company}`, label: 'Юридическое лицо' },
    ]), []);
    const options = useMemo(() => ([...touristOptions, ...typeOptions]), [touristOptions, typeOptions]);
    const currentSelection = useMemo(() => {
        var _a;
        if (customer.type === BookingCustomerType.Tourist && customer.touristKey)
            return `tourist:${customer.touristKey}`;
        return `type:${(_a = customer.type) !== null && _a !== void 0 ? _a : BookingCustomerType.Individual}`;
    }, [customer.type, customer.touristKey]);
    const selectedValue = useMemo(() => {
        var _a;
        if (currentSelection && options.some(o => o.value === currentSelection))
            return currentSelection;
        return (_a = options[0]) === null || _a === void 0 ? void 0 : _a.value;
    }, [currentSelection, options]);
    const onSelectionChange = (value) => {
        if (!value)
            return;
        if (value.startsWith('tourist:')) {
            props.onCustomerChange({
                type: BookingCustomerType.Tourist,
                touristKey: value.substring('tourist:'.length),
            });
        }
        else {
            const parsed = Number.parseInt(value.split(':')[1]) || BookingCustomerType.Individual;
            props.onCustomerChange({
                type: parsed,
                touristKey: null,
            });
        }
    };
    useEffect(() => {
        if (selectedValue && selectedValue !== currentSelection)
            onSelectionChange(selectedValue);
    }, [selectedValue, currentSelection]);
    const isTourist = (_c = selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.startsWith('tourist:')) !== null && _c !== void 0 ? _c : false;
    const type = isTourist ? BookingCustomerType.Tourist : (Number.parseInt((selectedValue || '').split(':')[1]) || BookingCustomerType.Individual);
    const showPersonal = !isTourist && (type === BookingCustomerType.Individual || type === BookingCustomerType.SoleProprietor);
    const showCompany = !isTourist && type === BookingCustomerType.Company;
    const showInnPersonal = !isTourist && type === BookingCustomerType.SoleProprietor;
    const showInnCompany = !isTourist && type === BookingCustomerType.Company;
    const showAddressField = !isTourist;
    const showContactFields = !isTourist;
    const defaultCitizenId = useMemo(() => {
        var _a, _b;
        const priority = (_b = (_a = props.configuration.citizenshipList) === null || _a === void 0 ? void 0 : _a.find(x => x.order == 1)) === null || _b === void 0 ? void 0 : _b.id;
        return priority || props.configuration.dbRussiaId;
    }, [props.configuration.citizenshipList, props.configuration.dbRussiaId]);
    useEffect(() => {
        if (isTourist || type === BookingCustomerType.Company)
            return;
        if (!customer.citizenCountryId && defaultCitizenId)
            props.onCustomerChange({ citizenCountryId: defaultCitizenId });
    }, [isTourist, type, customer.citizenCountryId, defaultCitizenId]);
    const useRussianDocuments = useMemo(() => {
        const services = props.booking.services || [];
        let baseService = null;
        if (isTourist && selectedValue) {
            const parts = selectedValue.split(':');
            const serviceId = Number.parseInt(parts[1]);
            baseService = services.find(s => s.id == serviceId);
        }
        if (!baseService && fallbackServiceId)
            baseService = services.find(s => s.id == fallbackServiceId);
        if (!baseService && services.length)
            baseService = services[0];
        if (!baseService)
            return false;
        return !!(baseService.isInternal || baseService.isRuInternal) || services.some(s => s.isInternal || s.isRuInternal);
    }, [props.booking.services, isTourist, selectedValue, fallbackServiceId]);
    const [docTypes, isRussian] = useMemo(() => {
        var _a;
        let russian = false;
        let use = useRussianDocuments;
        const citizenId = (_a = customer.citizenCountryId) !== null && _a !== void 0 ? _a : defaultCitizenId;
        if (citizenId && props.configuration.citizenshipList) {
            const citizenship = props.configuration.citizenshipList.find(x => x.id == citizenId);
            if (citizenship) {
                use = use && citizenship.id == props.configuration.dbRussiaId;
                russian = citizenship.id == props.configuration.dbRussiaId;
            }
        }
        return [getDocTypes(use, russian, 18, 18), russian];
    }, [useRussianDocuments, customer.citizenCountryId, defaultCitizenId, props.configuration.citizenshipList, props.configuration.dbRussiaId]);
    const visibleDocumentType = useMemo(() => {
        var _a;
        const selectedDocType = Number(customer.docType);
        return docTypes.some(doc => doc.value === selectedDocType)
            ? selectedDocType
            : (_a = docTypes[0]) === null || _a === void 0 ? void 0 : _a.value;
    }, [docTypes, customer.docType]);
    useEffect(() => {
        if (isTourist || type === BookingCustomerType.Company)
            return;
        if (visibleDocumentType === undefined || Number(customer.docType) === visibleDocumentType)
            return;
        props.onCustomerChange({ docType: visibleDocumentType });
    }, [visibleDocumentType, customer.docType, isTourist, type, props.onCustomerChange]);
    const showDocIssue = !isTourist && (visibleDocumentType === TouristDocumentType.RussianPassport || visibleDocumentType === TouristDocumentType.RussianBirthCertificate);
    const showDocExp = !isTourist && (visibleDocumentType === TouristDocumentType.RussianForeignPassport || visibleDocumentType === TouristDocumentType.ForeignDocument);
    const emailColClass = isTourist ? "col-md-6 col-sm-6 col-xs-12" : "col-md-3 col-sm-4 col-xs-12";
    const phoneColClass = isTourist ? "col-md-6 col-sm-6 col-xs-12" : "col-md-3 col-sm-4 col-xs-12";
    const syncAutofilledFields = (root) => {
        if (isTourist)
            return;
        const changes = {};
        const getValue = (id) => { var _a, _b; return (_b = (_a = root.querySelector(`#${id}`)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : ''; };
        const syncText = (id, field, normalize) => {
            var _a;
            const input = root.querySelector(`#${id}`);
            if (!input)
                return;
            const value = normalize ? normalize(input.value) : input.value;
            if (input.value != value)
                input.value = value;
            if (((_a = customer[field]) !== null && _a !== void 0 ? _a : '') != value)
                changes[field] = value;
        };
        const syncDate = (id, field) => {
            const date = parseAutofillDate(getValue(id));
            if (!date)
                return;
            const value = moment(date);
            if (!customer[field] || !moment(customer[field]).isSame(value, 'day'))
                changes[field] = value;
        };
        const normalizeName = (value) => value.replace(/[^a-zа-яё \-]/gi, '').toUpperCase();
        if (showPersonal) {
            syncText('customer-surname', 'surname', normalizeName);
            syncText('customer-name', 'name', normalizeName);
            syncText('customer-middlename', 'middleName', normalizeName);
            syncText('customer-docnumber', 'docNumber');
            syncDate('customer-docdate', 'docDate');
            syncDate('customer-docexpirationdate', 'docExpirationDate');
            if (showInnPersonal)
                syncText('customer-inn', 'inn');
        }
        if (showCompany) {
            syncText('customer-company-name', 'name');
            syncText('customer-inn-company', 'inn');
        }
        if (showAddressField)
            syncText('customer-address', 'address');
        if (showContactFields) {
            syncText('customer-email', 'email');
            syncText('customer-phone', 'phone');
        }
        if (Object.keys(changes).length)
            props.onCustomerChange(changes);
    };
    const autofillSync = useAutofillSync(syncAutofilledFields);
    return (React.createElement("div", Object.assign({ ref: autofillSync.rootRef }, autofillSync.autofillProps, { className: "contact-details ajax-replace react-cart-autofill-scope", id: "customer-details-wrapper" }),
        React.createElement("h3", null, "\u0414\u0430\u043D\u043D\u044B\u0435 \u0437\u0430\u043A\u0430\u0437\u0447\u0438\u043A\u0430"),
        React.createElement("div", { className: "customer-details-grid" },
            React.createElement("div", { className: "row" },
                React.createElement("div", { className: "col-md-6 col-sm-8 col-xs-12 customer-type-col" },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-type" }, "\u0417\u0430\u043A\u0430\u0437\u0447\u0438\u043A"),
                        React.createElement(BootstrapSelectPicker, { options: options, selected: selectedValue, labelSelector: "label", valueSelector: "value", liveSearch: true, selectProps: {
                                id: 'customer-type',
                                name: 'customer-type',
                                'data-default-adults': fallbackAdults || undefined,
                                'data-default-service': fallbackServiceId || undefined,
                            }, onChange: e => onSelectionChange(e) })))),
            React.createElement("div", { className: `row customer-personal ${showPersonal ? '' : 'hidden'}` },
                React.createElement("div", { className: "col-md-3 col-sm-6 col-xs-12" },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-surname" }, _T.COMMON_SURNAME),
                        React.createElement("input", { type: "text", className: "form-control", id: "customer-surname", name: "customer-surname", maxLength: 50, value: (_d = customer.surname) !== null && _d !== void 0 ? _d : '', onChange: e => props.onCustomerChange({ surname: e.target.value }) }))),
                React.createElement("div", { className: "col-md-3 col-sm-6 col-xs-12" },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-name" }, _T.COMMON_NAME),
                        React.createElement("input", { type: "text", className: "form-control", id: "customer-name", name: "customer-name", maxLength: 50, value: (_e = customer.name) !== null && _e !== void 0 ? _e : '', onChange: e => props.onCustomerChange({ name: e.target.value }) }))),
                React.createElement("div", { className: "col-md-3 col-sm-6 col-xs-12" },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-middlename" }, _T.COMMON_MIDDLE_NAME),
                        React.createElement("input", { type: "text", className: "form-control", id: "customer-middlename", name: "customer-middlename", maxLength: 50, value: (_f = customer.middleName) !== null && _f !== void 0 ? _f : '', onChange: e => props.onCustomerChange({ middleName: e.target.value }) }))),
                React.createElement("div", { className: "col-md-3 col-sm-6 col-xs-12" },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-citizen-country-id" }, _T.COMMON_CITIZENSHIP),
                        React.createElement(BootstrapSelectPicker, { options: props.configuration.citizenshipList, selected: (_g = customer.citizenCountryId) !== null && _g !== void 0 ? _g : defaultCitizenId, labelSelector: "name", valueSelector: "id", groupSelector: x => x.order == 1 ? 0 : 1, groupLabel: x => x.order == 1 ? _T.COMMON_OTHER_COUNTRIES : undefined, liveSearch: true, selectProps: {
                                id: 'customer-citizen-country-id',
                                name: 'customer-citizen-country-id',
                            }, onChange: e => props.onCustomerChange({ citizenCountryId: !e || typeof e == 'number' ? e || undefined : Number.parseInt(e) }) })))),
            React.createElement("div", { className: `row customer-doc ${showPersonal ? '' : 'hidden'}` },
                React.createElement("div", { className: "col-md-3 col-sm-6 col-xs-12" },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-doctype" }, "\u0422\u0438\u043F \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u0430"),
                        React.createElement(BootstrapSelectPicker, { options: docTypes, selected: visibleDocumentType, labelSelector: "name", valueSelector: "value", selectProps: {
                                id: 'customer-doctype',
                                name: 'customer-doctype',
                            }, onChange: e => props.onCustomerChange({ docType: !e || typeof e == 'number' ? e || undefined : Number.parseInt(e) }) }))),
                React.createElement("div", { className: "col-md-3 col-sm-6 col-xs-12" },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-docnumber" }, _T.COMMON_DOC_NUMBER),
                        React.createElement("input", { type: "text", className: "form-control", id: "customer-docnumber", name: "customer-docnumber", maxLength: 25, value: (_h = customer.docNumber) !== null && _h !== void 0 ? _h : '', onChange: e => props.onCustomerChange({ docNumber: e.target.value }) }))),
                React.createElement("div", { className: `col-md-3 col-sm-6 col-xs-12 customer-doc-issue ${showDocIssue ? '' : 'hidden'}` },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-docdate" }, _T.COMMON_ISSUE_DATE),
                        React.createElement(BootstrapDatetimePicker, { id: "customer-docdate", selected: customer.docDate ? moment(customer.docDate) : undefined, onChange: v => props.onCustomerChange({ docDate: v ? moment(v) : undefined }), inputProps: {
                                className: 'docdate',
                                name: 'customer-docdate',
                            }, mask: ['0Z.0Z.00ZZ', {
                                    translation: {
                                        'Z': {
                                            pattern: /[0-9]/,
                                            optional: true
                                        },
                                    },
                                    selectOnFocus: true,
                                }], maxDate: moment() }))),
                React.createElement("div", { className: `col-md-3 col-sm-6 col-xs-12 customer-doc-exp ${showDocExp ? '' : 'hidden'}` },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-docexpirationdate" }, _T.COMMON_EXP_DATE),
                        React.createElement(BootstrapDatetimePicker, { id: "customer-docexpirationdate", selected: customer.docExpirationDate ? moment(customer.docExpirationDate) : undefined, onChange: v => props.onCustomerChange({ docExpirationDate: v ? moment(v) : undefined }), inputProps: {
                                className: 'docexpirationdate',
                                name: 'customer-docexpirationdate',
                            }, mask: ['0Z.0Z.00ZZ', {
                                    translation: {
                                        'Z': {
                                            pattern: /[0-9]/,
                                            optional: true
                                        },
                                    },
                                    selectOnFocus: true,
                                }], minDate: moment() }))),
                React.createElement("div", { className: `col-md-3 col-sm-6 col-xs-12 customer-inn-doc ${showInnPersonal ? '' : 'hidden'}` },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-inn" }, _T.COMMON_INN),
                        React.createElement("input", { type: "text", className: "form-control", id: "customer-inn", name: "customer-inn", maxLength: 20, value: (_j = customer.inn) !== null && _j !== void 0 ? _j : '', onChange: e => props.onCustomerChange({ inn: e.target.value }) })))),
            React.createElement("div", { className: `row customer-company ${showCompany ? '' : 'hidden'}` },
                React.createElement("div", { className: "col-md-6 col-sm-8 col-xs-12 customer-company-name-col" },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-company-name" }, "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435"),
                        React.createElement("input", { type: "text", className: "form-control", id: "customer-company-name", name: "customer-company-name", maxLength: 200, value: (_k = customer.name) !== null && _k !== void 0 ? _k : '', onChange: e => props.onCustomerChange({ name: e.target.value }) }))),
                React.createElement("div", { className: `col-md-3 col-sm-4 col-xs-12 customer-inn-company ${showInnCompany ? '' : 'hidden'} customer-inn-company-col` },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-inn-company" }, _T.COMMON_INN),
                        React.createElement("input", { type: "text", className: "form-control", id: "customer-inn-company", name: "customer-inn-company", maxLength: 20, value: (_l = customer.inn) !== null && _l !== void 0 ? _l : '', onChange: e => props.onCustomerChange({ inn: e.target.value }) })))),
            React.createElement("div", { className: "row customer-address" },
                React.createElement("div", { className: `col-md-6 col-sm-8 col-xs-12 customer-address-field ${showAddressField ? '' : 'hidden'} customer-address-col` },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-address" }, _T.COMMON_ADDRESS),
                        React.createElement("input", { type: "text", className: "form-control", id: "customer-address", name: "customer-address", maxLength: 200, value: (_m = customer.address) !== null && _m !== void 0 ? _m : '', onChange: e => props.onCustomerChange({ address: e.target.value }) }))),
                React.createElement("div", { className: `${emailColClass} customer-email-col ${showContactFields ? '' : 'hidden'}` },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-email" }, _T.COMMON_EMAIL),
                        React.createElement("input", { type: "email", className: "form-control", id: "customer-email", name: "customer-email", maxLength: 100, value: (_o = customer.email) !== null && _o !== void 0 ? _o : '', onChange: e => props.onCustomerChange({ email: e.target.value }) }))),
                React.createElement("div", { className: `${phoneColClass} customer-phone-col ${showContactFields ? '' : 'hidden'}` },
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { htmlFor: "customer-phone" }, _T.COMMON_PHONE),
                        React.createElement("input", { type: "text", className: "form-control", id: "customer-phone", name: "customer-phone", maxLength: 50, value: (_p = customer.phone) !== null && _p !== void 0 ? _p : '', onChange: e => props.onCustomerChange({ phone: e.target.value }) })))))));
}
